import React, { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { modalScrollLock } from '../../utils/modalScrollLock';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClear?: () => void; // Clears form data (for X button or explicit cancel)
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  showCloseButton?: boolean;
  hasUnsavedChanges?: boolean; // M10: Warn before closing with unsaved changes
}

// M9: Custom focus trap hook - keeps Tab/Shift+Tab within the container
function useFocusTrap(containerRef: React.RefObject<HTMLElement | null>, isActive: boolean) {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isActive || !containerRef.current) return;

    if (e.key !== 'Tab') return;

    const container = containerRef.current;
    const focusableSelector =
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusableElements = Array.from(container.querySelectorAll<HTMLElement>(focusableSelector));

    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  }, [isActive, containerRef]);

  useEffect(() => {
    if (!isActive) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isActive, handleKeyDown]);
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  onClear,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  hasUnsavedChanges = false,
}) => {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);

  // M9: Apply focus trap
  useFocusTrap(modalRef, isOpen);

  // Focus and scroll lock: only on modal open/close, NOT on prop changes
  // This prevents focus stealing when hasUnsavedChanges changes (e.g., first keystroke)
  //
  // FIX: Use a ref to track whether we actually called lock(). Only unlock if we
  // locked. This prevents the global modalScrollLock counter from desyncing when
  // React.StrictMode double-mounts the component in development.
  const scrollLockActive = useRef(false);
  useEffect(() => {
    if (isOpen) {
      modalScrollLock.lock();
      scrollLockActive.current = true;
      // Focus the modal container only when it first opens
      setTimeout(() => {
        modalRef.current?.focus();
      }, 50);
    }

    return () => {
      if (scrollLockActive.current) {
        modalScrollLock.unlock();
        scrollLockActive.current = false;
      }
    };
  }, [isOpen]);

  // Escape key handler: needs current hasUnsavedChanges value
  // Separated from focus logic to avoid re-focusing on prop changes
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // M10: Check for unsaved changes before closing on Escape
        if (hasUnsavedChanges) {
          const confirmed = window.confirm(t('common.unsavedChangesClose'));
          if (!confirmed) return;
        }
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, hasUnsavedChanges]);

  if (!isOpen) return null;

  const sizes = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-xl',
    '2xl': 'sm:max-w-2xl',
  };

  // M10: Handle backdrop click with unsaved changes confirmation
  const handleBackdropClick = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(t('common.unsavedChangesClose'));
      if (!confirmed) return;
    }
    onClose();
  };

  // M10: Handle close button with unsaved changes confirmation
  const handleCloseClick = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(t('common.unsavedChangesClose'));
      if (!confirmed) return;
    }
    if (onClear) {
      onClear();
      onClose();
    } else {
      onClose();
    }
  };

  return createPortal(
    <>
      {/* Backdrop - high z-index to cover everything */}
      <div
        className="fixed inset-0 z-[100] bg-black/50 transition-opacity"
        onClick={handleBackdropClick}
      />

      {/* Modal container - higher z-index */}
      <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center pointer-events-none">
        {/* Modal content - pointer-events-auto to allow clicks */}
        <div
          ref={modalRef}
          tabIndex={-1}
          className={`
            pointer-events-auto modal-scroll-lock
            relative w-full ${sizes[size]} bg-white dark:bg-gray-900
            rounded-t-2xl sm:rounded-xl shadow-xl transform transition-all
            max-h-[95vh] sm:max-h-[90vh]
            overflow-hidden flex flex-col pb-[env(safe-area-inset-bottom)]
          `}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? 'modal-title' : undefined}
        >
          {/* Header */}
          {(title || showCloseButton) && (
            <div className="relative flex items-center justify-between px-4 sm:px-6 pt-4 sm:pt-6 pb-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              {/* Mobile drag handle indicator */}
              <div className="absolute left-1/2 -translate-x-1/2 top-2 w-10 h-1 bg-gray-300 dark:bg-gray-700 rounded-full sm:hidden" />
              {title && (
                <h2
                  id="modal-title"
                  className="text-lg font-semibold text-gray-900 dark:text-gray-100"
                >
                  {title}
                </h2>
              )}
              {showCloseButton && (
                <button
                  onClick={handleCloseClick}
                  className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
                  aria-label={t('common.close')}
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          )}

          {/* Body */}
          <div className="px-4 sm:px-6 py-4 overflow-y-auto flex-1">
            {children}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default Modal;
